import type { IndicationOutput, PermanentScenarioOutput } from '@zg/shared';
import { Eyebrow } from '../Eyebrow';
import { formatCurrency } from '../../lib/format';

export interface PermanentSectionProps {
  permanent: IndicationOutput['permanent'];
}

function Column({ title, code, data }: { title: string; code: string; data: PermanentScenarioOutput }) {
  return (
    <div className="bg-panel border border-hairline rounded-md p-6 flex flex-col">
      <span className="font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle">
        &gt; {code}
      </span>
      <h3 className="mt-2 font-display font-medium text-[1.25rem] text-ink">{title}</h3>
      <dl className="mt-5 divide-y divide-hairline">
        <div className="flex items-baseline justify-between py-3 text-[0.9375rem]">
          <dt className="text-ink-muted">Property</dt>
          <dd className="font-tnum font-medium">{formatCurrency(data.property.total)}</dd>
        </div>
        <div className="flex items-baseline justify-between py-3 text-[0.9375rem]">
          <dt className="text-ink-muted">General Liability</dt>
          <dd className="font-tnum font-medium">{formatCurrency(data.gl.total)}</dd>
        </div>
        <div className="flex items-baseline justify-between pt-4">
          <dt className="font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle">
            &gt; TOTAL
          </dt>
          <dd className="font-display font-light font-tnum text-[clamp(24px,3vw,32px)] text-ink leading-none">
            {formatCurrency(data.total)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

export function PermanentSection({ permanent }: PermanentSectionProps) {
  return (
    <section className="container-site py-16 md:py-20">
      <Eyebrow>LOOKING_AHEAD · PERMANENT_INSURANCE</Eyebrow>
      <h2 className="mt-4 font-display font-medium tracking-[-0.015em] leading-[1.15] text-[clamp(28px,4vw,44px)] max-w-2xl">
        After the building opens.
      </h2>
      <p className="mt-4 max-w-2xl text-[1rem] leading-relaxed text-ink-muted">
        Builders risk ends when the building opens. Here&apos;s a look at what permanent coverage
        typically runs for a project of this size.
      </p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
        <Column title="Aggressive" code="PERM_AGGRESSIVE" data={permanent.aggressive} />
        <Column title="Conservative" code="PERM_CONSERVATIVE" data={permanent.conservative} />
      </div>
    </section>
  );
}
